import { z } from 'zod';
import { UuidSchema, IsoDateSchema, IsoDateTimeSchema } from '../common/ids.js';
import { ExceptionTypeSchema, ExceptionSeveritySchema, ExceptionStatusSchema, PunchSourceSchema, } from '../common/enums.js';
import { PaginationQuerySchema } from '../common/pagination.js';
import { DiscoveryQuerySchema } from '../common/discovery.js';
/**
 * The worker an exception is about, denormalized onto the row (Phase 3 batch,
 * 2026-08-28).
 *
 * `employeeNumber` is here and `LinkedUserSchema` (the detail view's) does not
 * have it: a triage queue is scanned by employee number, and the alternative —
 * one `GET /users/:id` per row — is the N+1 this field exists to remove.
 */
export const ExceptionUserSummarySchema = z.object({
    id: UuidSchema,
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    employeeNumber: z.string().min(1),
});
export const ExceptionSchema = z.object({
    id: UuidSchema,
    type: ExceptionTypeSchema,
    severity: ExceptionSeveritySchema,
    contractId: UuidSchema,
    siteId: UuidSchema.nullable(),
    userId: UuidSchema.nullable(),
    shiftId: UuidSchema.nullable(),
    punchId: UuidSchema.nullable(),
    businessDate: IsoDateSchema,
    /** Structured, localizable payload (Track E: "Never store the sentence."). */
    details: z.unknown(),
    dedupeKey: z.string().min(1),
    status: ExceptionStatusSchema,
    resolvedBy: UuidSchema.nullable(),
    resolvedAt: IsoDateTimeSchema.nullable(),
    /** Mandatory when `status = dismissed`; the length rule is enforced by
     * `DismissRequestSchema` on write, not re-checked on read. */
    resolutionNote: z.string().nullable(),
    /** Optional (Phase 1 change batch, 2026-08-27, Track E): a localized
     * sentence rendered at read time from `type`/`details` — never stored,
     * so absent means "not composed for this response," not "no message." */
    description: z.string().optional(),
    // ---- list/detail enrichment (Phase 3 batch, 2026-08-28) ----------------
    //
    // All `.optional()`, all resolved at read time from ids this row already
    // carries. The triage queue renders a worker's name, a site and a zone on
    // every line; without these it issued three lookups per row against
    // endpoints it then had to re-scope. Absent means "not resolved for this
    // response"; `null` means "this row has no such link".
    /** The worker `userId` names. `null` when the exception is not about one. */
    user: ExceptionUserSummarySchema.nullable().optional(),
    /** The name of `siteId`. `null` for a contract-level exception (site-less). */
    siteName: z.string().min(1).nullable().optional(),
    /**
     * The zone of the linked punch, falling back to the linked shift's.
     * `null` whenever neither names one — `exceptions` has no zone column, and
     * most exception types are not about a zone at all.
     */
    zoneName: z.string().min(1).nullable().optional(),
    /**
     * When the detector raised it — `exceptions.created_at`.
     *
     * Named `raisedAt` rather than `createdAt` because that is the event it
     * marks, and it is a different question from `businessDate` (the day the
     * work belongs to) and from `resolvedAt`. An overnight detector run can
     * raise a Tuesday exception on Wednesday morning, and a queue sorted by the
     * wrong one of the two puts the newest problems in the middle.
     */
    raisedAt: IsoDateTimeSchema.optional(),
    /** Display name of `resolvedBy`. `null` while the exception is still open. */
    resolvedByName: z.string().min(1).nullable().optional(),
});
/**
 * `GET /exceptions` filters (03-API-CONTRACT.md §6): `type`, `severity`,
 * `status` are narrowed to their real enums here, overriding the generic
 * (untyped) `status` string from `DiscoveryQuerySchema`.
 */
export const ExceptionListQuerySchema = DiscoveryQuerySchema.extend(PaginationQuerySchema.shape).extend({
    type: ExceptionTypeSchema.optional(),
    severity: ExceptionSeveritySchema.optional(),
    status: ExceptionStatusSchema.optional(),
    /**
     * Lifecycle side, coarser than `status` (additive, 2026-09-03): `open` =
     * `open` or `acknowledged` (the triage queue's default scope — everything
     * still needing a decision), `closed` = `resolved` or `dismissed`. Ignored
     * when `status` is present, which is already exact.
     */
    state: z.enum(['open', 'closed']).optional(),
    /**
     * Every exception raised against one punch (Phase 3 batch, 2026-08-28) —
     * the pivot from a punch detail view into "what did this punch trip".
     */
    punchId: UuidSchema.optional(),
    /**
     * The **linked punch's** `source`, not a column on `exceptions` — there is
     * none (Phase 3 batch, 2026-08-28). It answers the one triage question the
     * stored columns cannot: "is this queue full because the offline batch came
     * in badly, or because something happened on site". A row with no linked
     * punch is excluded by this filter, since it has no source to match.
     */
    source: PunchSourceSchema.optional(),
});
const LinkedPunchSchema = z.object({
    id: UuidSchema,
    type: z.enum(['IN', 'OUT']),
    recordedAt: IsoDateTimeSchema,
});
const LinkedShiftSchema = z.object({
    id: UuidSchema,
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
});
const LinkedUserSchema = z.object({
    id: UuidSchema,
    firstName: z.string().min(1),
    lastName: z.string().min(1),
});
/** `GET /exceptions/:id` — detail with linked punch/shift/worker. */
export const ExceptionDetailSchema = ExceptionSchema.extend({
    linkedPunch: LinkedPunchSchema.nullable(),
    linkedShift: LinkedShiftSchema.nullable(),
    linkedUser: LinkedUserSchema.nullable(),
});
export const AcknowledgeRequestSchema = z.object({}).strict();
export const ResolveRequestSchema = z.object({
    note: z.string().trim().min(1).optional(),
});
/** `note` mandatory — "dismissing without a reason is how problems disappear silently." */
export const DismissRequestSchema = z.object({
    note: z.string().trim().min(1),
});
/**
 * `POST /exceptions/bulk-resolve` (Track E: "Bulk resolve permitted for
 * `low` severity only"). That restriction needs the exceptions' current
 * severity, which this schema cannot see from `ids` alone — it is enforced
 * server-side. See README "Ambiguity resolutions".
 */
export const BulkResolveRequestSchema = z.object({
    ids: z.array(UuidSchema).min(1),
    note: z.string().trim().min(1).optional(),
});
/** `GET /exceptions/summary` — counts by type/severity for the dashboard. */
export const ExceptionSummaryResponseSchema = z.object({
    total: z.int().min(0),
    bySeverity: z.record(ExceptionSeveritySchema, z.int().min(0)),
    byType: z.record(ExceptionTypeSchema, z.int().min(0)),
});
/**
 * `POST /exceptions/bulk-resolve` response (Phase 1 change batch,
 * 2026-08-27, Track E). `skipped` carries every id the server declined to
 * resolve (e.g. severity above `low`) with a human-readable reason, so the
 * caller can reconcile its selection against what actually happened.
 */
export const ExceptionBulkResolveResponseSchema = z.object({
    resolved: z.int().min(0),
    skipped: z.array(z.object({
        id: UuidSchema,
        reason: z.string().min(1),
    })),
});
//# sourceMappingURL=exception.js.map